import React, { useState, useCallback } from 'react';
import { 
  ChevronLeftIcon, 
  ChevronRightIcon, 
  TrashIcon, 
  CheckCircleIcon,
  XCircleIcon 
} from '@heroicons/react/24/solid';
import { DuplicateGroup } from '@/utilities/duplicateGroupUtils';
import { Node, Beam } from '@/types/nnstructural2D';

interface Props<T> {
  isOpen: boolean;
  onClose: () => void;
  onFinish: (resolutions: Record<number, Array<{ source: 'existing' | 'imported'; index: number }>>) => void;
  groups: DuplicateGroup<T>[];
  itemType: 'nodes' | 'beams';
  title: string;
  importMode?: 'replace' | 'append';
}

export function DuplicateGroupReviewDialog<T extends Node | Beam>({
  isOpen,
  onClose,
  onFinish,
  groups,
  itemType,
  title,
  importMode = 'replace'
}: Props<T>) {
  const [currentGroupIndex, setCurrentGroupIndex] = useState(0);
  const [resolutions, setResolutions] = useState<Record<number, Array<{ source: 'existing' | 'imported'; index: number }>>>({});
  const [initialized, setInitialized] = useState(false);

  // Initialize resolutions for all groups when component mounts or groups change
  React.useEffect(() => {
    if (isOpen && groups.length > 0 && !initialized) {
      const initialResolutions: Record<number, Array<{ source: 'existing' | 'imported'; index: number }>> = {};
      
      groups.forEach(group => {
        // For append mode, default to keeping existing items and discarding imported duplicates
        if (importMode === 'append') {
          initialResolutions[group.id] = group.items
            .filter(item => item.source === 'existing')
            .map(item => ({ source: item.source, index: item.originalIndex || 0 }));
        } else {
          // For replace mode, default to keeping the first item in the group
          const firstItem = group.items[0];
          initialResolutions[group.id] = [{
            source: firstItem.source,
            index: firstItem.originalIndex || 0
          }];
        }
      });
      
      setResolutions(initialResolutions);
      setInitialized(true);
    }
  }, [isOpen, groups, initialized, importMode]);

  // Reset state when dialog closes
  React.useEffect(() => {
    if (!isOpen) {
      setCurrentGroupIndex(0);
      setResolutions({});
      setInitialized(false);
    }
  }, [isOpen]);

  const handleDeleteItem = useCallback((itemIndex: number) => {
    if (!groups.length) return;
    const currentGroup = groups[currentGroupIndex];
    if (!currentGroup) return;
    const currentResolution = resolutions[currentGroup.id] || [];
    const item = currentGroup.items[itemIndex];
    
    // Remove this item from the resolution
    const newResolution = currentResolution.filter(r => 
      !(r.source === item.source && 
        (item.source === 'existing' || r.index === item.originalIndex))
    );
    
    setResolutions(prev => ({
      ...prev,
      [currentGroup.id]: newResolution
    }));
  }, [groups, currentGroupIndex, resolutions]);

  const handleKeepItem = useCallback((itemIndex: number) => {
    if (!groups.length) return;
    const currentGroup = groups[currentGroupIndex];
    if (!currentGroup) return;
    const currentResolution = resolutions[currentGroup.id] || [];
    const item = currentGroup.items[itemIndex];
    
    // Add this item to the resolution if not already there
    const itemChoice = {
      source: item.source,
      index: item.originalIndex || 0
    };
    
    const isAlreadyKept = currentResolution.some(r => 
      r.source === item.source && 
      (item.source === 'existing' || r.index === item.originalIndex)
    );
    
    if (!isAlreadyKept) {
      setResolutions(prev => ({
        ...prev,
        [currentGroup.id]: [...currentResolution, itemChoice]
      }));
    }
  }, [groups, currentGroupIndex, resolutions]);

  const goToNextGroup = () => {
    if (currentGroupIndex < groups.length - 1) {
      setCurrentGroupIndex(prev => prev + 1);
    }
  };

  const goToPreviousGroup = () => {
    if (currentGroupIndex > 0) {
      setCurrentGroupIndex(prev => prev - 1);
    }
  };

  const canProceedToNext = () => {
    if (!groups.length) return false;
    const currentGroup = groups[currentGroupIndex];
    if (!currentGroup) return false;
    const currentResolution = resolutions[currentGroup.id];
    return currentResolution && currentResolution.length > 0;
  };

  const handleFinish = () => {
    onFinish(resolutions);
  };

  const renderItemValue = (item: T) => {
    if (itemType === 'nodes') {
      const node = item as Node;
      return `(${node.x.toFixed(2)}, ${node.y.toFixed(2)}, ${node.z.toFixed(2)})`;
    } else {
      const beam = item as Beam;
      return `${beam.startNodeId} → ${beam.endNodeId}`;
    }
  };

  const renderItemDetails = (item: T) => {
    if (itemType === 'nodes') {
      const node = item as Node;
      return (
        <div className="grid grid-cols-4 gap-2 text-sm">
          <div><strong>ID:</strong> {node.id}</div>
          <div><strong>X:</strong> {node.x.toFixed(3)}</div>
          <div><strong>Y:</strong> {node.y.toFixed(3)}</div>
          <div><strong>Z:</strong> {node.z.toFixed(3)}</div>
        </div>
      );
    } else {
      const beam = item as Beam;
      return (
        <div className="grid grid-cols-4 gap-2 text-sm">
          <div><strong>ID:</strong> {beam.id}</div>
          <div><strong>Start:</strong> {beam.startNodeId}</div>
          <div><strong>End:</strong> {beam.endNodeId}</div>
          <div><strong>Material:</strong> {beam.material}</div>
        </div>
      );
    }
  };

  if (!isOpen || groups.length === 0) return null;

  const currentGroup = groups[currentGroupIndex];
  const isLastGroup = currentGroupIndex === groups.length - 1;
  const isFirstGroup = currentGroupIndex === 0;
  const currentResolution = resolutions[currentGroup?.id] || [];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden">
  {/* Header */}
  <div className={`p-4 ${importMode === 'append' ? 'bg-blue-100 text-black' : 'bg-blue-600 text-white'}`}>
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-bold">{title}</h2>
            <button
              onClick={onClose}
              className={importMode === 'append' ? 'text-black hover:text-gray-700' : 'text-white hover:text-gray-200'}
            >
              <XCircleIcon className="h-6 w-6" />
            </button>
          </div>
          <div className="mt-2 flex justify-between items-center text-sm">
            <span>Group {currentGroupIndex + 1} of {groups.length}</span>
            <span className="bg-blue-700 px-2 py-1 rounded">
              {currentGroup.items.length} duplicates found
            </span>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          <div className="mb-4">
            <h3 className={`text-lg font-semibold mb-2 ${importMode === 'append' ? 'text-black' : 'text-gray-900'}`}>
              Duplicate Group: {renderItemValue(currentGroup.items[0].item)}
            </h3>
            <p className={`text-sm mb-4 ${importMode === 'append' ? 'text-black' : 'text-gray-600'}`}>
              The following {itemType} have been identified as duplicates. 
              Click the delete icon to remove an item, or it will be kept by default.
            </p>
          </div>

          {/* Items in current group */}
          <div className="space-y-3">
            {currentGroup.items.map((groupItem, index) => {
              const isKept = currentResolution.some(r => 
                r.source === groupItem.source && 
                (groupItem.source === 'existing' || r.index === groupItem.originalIndex)
              );
              
              return (
                <div 
                  key={index} 
                  className={`border rounded-lg p-4 transition-all ${
                    isKept 
                      ? 'border-green-300 bg-green-50' 
                      : 'border-red-300 bg-red-50'
                  }`}
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          groupItem.source === 'existing' 
                            ? 'bg-blue-100 text-blue-800'
                            : 'bg-orange-100 text-orange-800'
                        }`}>
                          {groupItem.source === 'existing' ? 'Existing' : 'Imported'}
                        </span>
                        {isKept ? (
                          <CheckCircleIcon className="h-5 w-5 text-green-600" />
                        ) : (
                          <TrashIcon className="h-5 w-5 text-red-600" />
                        )}
                      </div>
                      {renderItemDetails(groupItem.item)}
                    </div>
                    
                    <div className="ml-4">
                      {isKept ? (
                        <button
                          onClick={() => handleDeleteItem(index)}
                          className="p-2 text-red-600 hover:bg-red-100 rounded"
                          title="Delete this item"
                        >
                          <TrashIcon className="h-5 w-5" />
                        </button>
                      ) : (
                        <button
                          onClick={() => handleKeepItem(index)}
                          className="p-2 text-green-600 hover:bg-green-100 rounded"
                          title="Keep this item"
                        >
                          <CheckCircleIcon className="h-5 w-5" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Warning if no items selected */}
          {currentResolution.length === 0 && (
            <div className="mt-4 p-3 bg-yellow-100 border border-yellow-300 rounded text-yellow-800 text-sm">
              ⚠️ Warning: At least one item must be kept from each duplicate group.
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-6 py-4 flex justify-between items-center">
          <button
            onClick={goToPreviousGroup}
            disabled={isFirstGroup}
            className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-800 disabled:text-gray-400 disabled:cursor-not-allowed"
          >
            <ChevronLeftIcon className="h-5 w-5" />
            Previous
          </button>

          <span className="text-sm text-gray-600">
            {currentResolution.length} of {currentGroup.items.length} items will be kept
          </span>

          {isLastGroup ? (
            <button
              onClick={handleFinish}
              disabled={!canProceedToNext()}
              className="flex items-center gap-2 px-6 py-2 bg-green-600 text-black rounded hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              Finish
            </button>
          ) : (
            <button
              onClick={goToNextGroup}
              disabled={!canProceedToNext()}
              className="flex items-center gap-2 px-4 py-2 text-blue-600 hover:text-blue-800 disabled:text-gray-400 disabled:cursor-not-allowed"
            >
              Next
              <ChevronRightIcon className="h-5 w-5" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
